import { ElementRef, Injectable } from '@angular/core';

import {
  fromEvent as observableFromEvent,
  merge as observableMerge,
  Subject,
  Subscription,
} from 'rxjs';
import { auditTime } from 'rxjs/operators';

import { IScrollable } from './interface';

export const DEFAULT_SCROLL_TIME = 0;

/**
 * Service contained all registered Scrollable references and emits an event when any one of the
 * Scrollable references emit a scrolled event.
 */
@Injectable({ providedIn: 'root' })
export class ScrollDispatcher {
  /** Subject for notifying that a registered scrollable reference element has been scrolled. */
  private _scrolled = new Subject<void>();
  /** Keeps track of the global `scroll` and `resize` subscriptions. */
  private _globalSubscription: Subscription | null = null;
  /**
   * Map of all the scrollable references that are registered with the service and their
   * scroll event subscriptions.
   */
  private scrollableReferences: Map<IScrollable, Subscription> = new Map();
  /** Keeps track of the amount of subscriptions to `scrolled`. Used for cleaning up afterwards. */
  private _scrolledCount: number = 0;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  /**
   * Registers a Scrollable with the service and listens for its scrolled events. When the
   * scrollable is scrolled, the service emits the event in its scrolled observable.
   * @param scrollable Scrollable instance to be registered.
   */
  public register(scrollable: IScrollable): void {
    const scrollSubscription = scrollable.elementScrolled().subscribe(() => this._notify());
    this.scrollableReferences.set(scrollable, scrollSubscription);
  }

  /**
   * Deregisters a Scrollable reference and unsubscribes from its scroll event observable.
   * @param scrollable Scrollable instance to be deregistered.
   */
  public deregister(scrollable: IScrollable): void {
    if (this.scrollableReferences.has(scrollable)) {
      this.scrollableReferences.get(scrollable)!.unsubscribe();
      this.scrollableReferences.delete(scrollable);
    }
  }

  /**
   * Subscribes to an observable that emits an event whenever any of the registered Scrollable
   * references (or window, document, or body) fire a scrolled event. Can provide a time in ms
   * to override the default "throttle" time.
   */
  public scrolled(auditTimeInMs: number = DEFAULT_SCROLL_TIME, callback: () => any): Subscription {
    // In the case of a 0ms delay, use an observable without auditTime
    // since it does add a perceptible delay in processing overhead.
    const observable =
      auditTimeInMs > 0
        ? this._scrolled.asObservable().pipe(auditTime(auditTimeInMs))
        : this._scrolled.asObservable();

    this._scrolledCount++;

    if (!this._globalSubscription) {
      this._globalSubscription = observableMerge(
        observableFromEvent(window.document, 'scroll'),
        observableFromEvent(window, 'resize'),
      ).subscribe(() => this._notify());
    }

    // Note that we need to do the subscribing from here, in order to be able to remove
    // the global event listeners once there are no more subscriptions.
    const subscription = observable.subscribe(callback);

    subscription.add(() => {
      this._scrolledCount--;

      if (this._globalSubscription && !this.scrollableReferences.size && !this._scrolledCount) {
        this._globalSubscription.unsubscribe();
        this._globalSubscription = null;
      }
    });

    return subscription;
  }

  /** Returns all registered Scrollables that contain the provided element. */
  public getScrollContainers(elementRef: ElementRef): IScrollable[] {
    const scrollingContainers: IScrollable[] = [];

    this.scrollableReferences.forEach((subscription: Subscription, scrollable: IScrollable) => {
      if (this.scrollableContainsElement(scrollable, elementRef)) {
        scrollingContainers.push(scrollable);
      }
    });

    return scrollingContainers;
  }

  /** Returns true if the element is contained within the provided Scrollable. */
  private scrollableContainsElement(scrollable: IScrollable, elementRef: ElementRef): boolean {
    let element = elementRef.nativeElement;
    const scrollableElement = scrollable.getElementRef().nativeElement;

    // Traverse through the element parents until we reach null, checking if any of the elements
    // are the scrollable's element.
    do {
      if (element === scrollableElement) {
        return true;
      }
    } while ((element = element.parentElement));

    return false;
  }

  /** Sends a notification that a scroll event has been fired. */
  private _notify(): void {
    this._scrolled.next();
  }
}
